import {Injectable} from '@angular/core';
import {firstValueFrom, map, Observable, of} from 'rxjs';
import {Apollo, gql} from 'apollo-angular';
import {LoginInput, RefreshPayload} from '@graphql/generated';
import {LOGIN_QUERY, ME_QUERY, REFRESH_TOKEN_QUERY, SEND_OTP_QUERY, WHITELABEL_FOR_USER} from '../graphql/auth.query';
import {FORGOT_PASSWORD_MUTATION, RESET_PASSWORD_MUTATION, VERIFY_OTP_MUTATION} from '../graphql/auth.mutation'
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {
  clearOrganisationContext,
  setOrganisationUser,
  setSelectedOrganisation
} from '@shared/store/organisation/organisation.actions';
import {setWhitelabel} from '@shared/store/app/app.action';
import {selectWhitelabelSource} from '@shared/store/app/app.selector';
import {setPermissions} from '@shared/store/permissions/permissions.action';
import {ImpersonationService} from '@shared/services/impersonation.service';

// Add the new query constant
const EMAIL_OR_MOBILE_BASED_ORGANISATIONS_QUERY = gql`
  query EmailOrMobileBasedOrganisations($emailOrMobile: String!, $webDomain: String) {
    EmailOrMobileBasedOrganisations(emailOrMobile: $emailOrMobile, webDomain: $webDomain) {
      id
      createdAt
      updatedAt
      name
      isActive
      parentOrganisationID
      rootOrganisationID
      organisationLimit
      deviceLimit
      availableLicenses
      totalLicenses
      billingAddressLine1
      billingAddressLine2
      billingAddressCity
      billingAddressState
      billingAddressCountry
      billingGstin
      billingEmail
      billingEnabled
      allowChildCreation
      centralisedWallet
      boltLegacyID
    }
  }
`;

// New queries for mobile OTP login - simplified to avoid schema errors
const SEND_MOBILE_OTP_QUERY = gql`
  query SendOtp($mobileNumber: String!, $organisationId: ID!) {
    SendOtp(mobileNumber: $mobileNumber, organisationId: $organisationId)
  }
`;

const OTP_LOGIN_QUERY = gql`
  query OtpLogin($mobileNumber: String!, $otp: String!, $organisationId: ID!, $platformUniqueId: String!, $platformFriendlyName: String!) {
    OtpLogin(mobileNumber: $mobileNumber, otp: $otp, organisationId: $organisationId, platformUniqueId: $platformUniqueId, platformFriendlyName: $platformFriendlyName) {
      token
      permissions {
        name
        create
        read
        edit
        delete
      }
      organisationUser {
        id
        userID
        user {
          id
          firstName
          lastName
          email
          role
        }
      }
    }
  }
`;

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  constructor(
    private apollo: Apollo,
    private router: Router,
    private store: Store,
    private impersonationService: ImpersonationService
  ) { }

  login(input: LoginInput): Observable<any> {
    return this.apollo.query({
      query: LOGIN_QUERY,
      variables: {input},
      context: {
        customHeaders: {
          skipAuth: true // <-- pass flag to skip auth token logic
        }
      }
    }).pipe(
      map((result: any) => {
        return result
      })
    )
  }

  passwordLogin(email: string, password: string, organisationId: string, platformUniqueId: string, platformFriendlyName: string): Observable<any> {
    return this.apollo.use('mesh').query({
      query: gql`
        query PasswordLogin($email: String!, $password: String!, $organisationId: ID!, $platformUniqueId: String!, $platformFriendlyName: String!) {
          PasswordLogin(email: $email, password: $password, organisationId: $organisationId, platformUniqueId: $platformUniqueId, platformFriendlyName: $platformFriendlyName){
            token
            organisationUser {
            userID
              id
              role {
                organisationRolePermissions {
                  permission {
                    name
                  }
                  create
                  delete
                }
              }
              user {
                id
                firstName
                lastName
                email
                role
              }
              organisationID
            }
          }
        }`,
      variables: {
        email,
        password,
        organisationId,
        platformUniqueId,
        platformFriendlyName,
      },
      context: {
        customHeaders: {
          skipAuth: true // <-- pass flag to skip auth token logic
        }
      }
    }).pipe(
      map((result: any) => {
        return result
      })
    )
  }

  // New method to get organizations based on email or mobile
  getOrganisationsByEmailOrMobile(emailOrMobile: string): Observable<any> {
    const url = new URL(window.location.href);
    const domain = `${url.protocol}//${url.hostname}`;

    return this.apollo.use('mesh').query({
      query: EMAIL_OR_MOBILE_BASED_ORGANISATIONS_QUERY,
      variables: {
        emailOrMobile,
        webDomain: domain
      },
      context: {
        customHeaders: {
          skipAuth: true
        }
      }
    }).pipe(
      map((result: any) => {
        // Return the full result including errors
        if (result.errors && result.errors.length > 0) {
          return { errors: result.errors, data: result.data };
        }
        if (result.data && result.data.EmailOrMobileBasedOrganisations) {
          return result.data.EmailOrMobileBasedOrganisations;
        }
        return [];
      })
    );
  }

  sendMobileOtp(mobileNumber: string, organisationId: string): Observable<any> {
    return this.apollo.use('mesh').query({
      query: SEND_MOBILE_OTP_QUERY,
      variables: {
        mobileNumber,
        organisationId
      },
      context: {
        customHeaders: {
          skipAuth: true
        }
      }
    }).pipe(
      map((result: any) => {
        if (result.errors && result.errors.length > 0) {
          return { errors: result.errors, data: result.data };
        }
        return result;
      })
    );
  }

  // New method for OTP login with mobile number
  otpLogin(mobileNumber: string, otp: string, organisationId: string, platformUniqueId: string, platformFriendlyName: string): Observable<any> {
    return this.apollo.use('mesh').query({
      query: OTP_LOGIN_QUERY,
      variables: {
        mobileNumber,
        otp,
        organisationId,
        platformUniqueId,
        platformFriendlyName
      },
      context: {
        customHeaders: {
          skipAuth: true
        }
      }
    }).pipe(
      map((result: any) => {
        if (result.errors && result.errors.length > 0) {
          return { errors: result.errors, data: result.data };
        }
        return result;
      })
    );
  }

  sendOtp(identifier: string, mode: "email" | "mobile", whitelabelID: string): Observable<any> {
    return this.apollo.query({
      query: SEND_OTP_QUERY,
      variables: {
        input: {
          [mode]: identifier, // Dynamically pass email
          mode: "otp",
          whitelabelID
        },
      },
    });
  }


  refreshToken(refreshTokenPayload: RefreshPayload): Observable<any> {
    return this.apollo.query({
      query: REFRESH_TOKEN_QUERY,
      variables: {
        input: refreshTokenPayload
      },
      context: {
        customHeaders: {
          skipAuth: true // <-- pass flag to skip auth token logic
        }
      }
    });
  }

  me(): Observable<any> {
    return this.apollo.query({
      query: ME_QUERY,
      context: {
        customHeaders: {
          skipOrg: true
        }
      }
    })
  }

  getWhitelabelsForUser(identifier, identifierType): Observable<any> {
    return this.apollo.query({
      query: WHITELABEL_FOR_USER,
      variables: {input: {identifier, identifierType}}
    }).pipe(
      map((result: any) => {
        return result.data.getWhitelabelOrgsForUser;
      })
    )
  }

  async logout() {
    // Clear organization and permission related state
    this.store.dispatch(setOrganisationUser({ organisationUser: null }))
    this.store.dispatch(setSelectedOrganisation({ selectedOrganisation: null }))
    this.store.dispatch(setPermissions({ permissions: null, permissionScope: 'user'}))

    // Handle whitelabel state
    const source = await firstValueFrom(this.store.select(selectWhitelabelSource));
    if (source && source == 'login') {
      this.store.dispatch(setWhitelabel({whitelabelDomainInfo: null, whitelabelSource: null}))
    }

    // Check if we're logging out from impersonated session
    if (this.impersonationService.isImpersonating()) {
      // For impersonated user logout, use blank page with force reload to restore admin session
      this.impersonationService.handleLogout();
      this.router.navigateByUrl('/blank-page?force_reload=true&return_to_admin=true');
    } else {
      // For admin logout, clear organisation context as well
      this.store.dispatch(clearOrganisationContext());

      // For admin logout, go directly to login
      const redirectUrl = this.impersonationService.handleLogout();
      this.router.navigateByUrl(redirectUrl);
    }
  }

  getPermissionsFromApi(): Observable<any> {
    // Will be implemented once the permissions API is available
    return of({});
  }

  forgotPassword(email: string, whitelabelID: string){
    return this.apollo.mutate({
      mutation: FORGOT_PASSWORD_MUTATION,
      variables:{email, whitelabelID}
    }).pipe(
      map((result: any) => {
        if (result.errors) {
          throw result.errors[0].extensions.errors.token[0]; // Ensure errors are passed to `error` block
        }
        return result.data;
      })
    )
  }

  resetPassword(token: string, password: string) {
    return this.apollo.mutate({
      mutation: RESET_PASSWORD_MUTATION,
      variables: { token, password }
    }).pipe(
      map((result: any) => {
        if (result?.errors) {
          return { errors: result.errors };
        }
        return result;
      })
    );
  }


  forgotPasswordverifyOtp(otp: string, email: string, whitelabelID) {
    return this.apollo.mutate({
      mutation: VERIFY_OTP_MUTATION,
      variables: {
        otp,
        email,
        whitelabelID
      }
    }).pipe(
      map((result: any) => {
        if (result?.errors) {
          throw new Error(result.errors.map((e: any) => e.message).join(', '));
        }
        return result.data.forgotPasswordVerifyOtp; // Ensure only valid data is returned
      })
    );
  }

}
